import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Table, Button, Input, InputGroup } from 'reactstrap';
import { Eye, Pause, Play, XCircle, Copy, Plus, Search, TrendingUp, Target, Send, CheckCircle, Edit2, Trash2, Archive } from 'react-feather';
import CountUp from 'react-countup';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro, alertConfirmar, alertSucesso } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

const STATUS_LABEL = {
  draft: 'Rascunho', scheduled: 'Agendada', running: 'Em disparo',
  paused: 'Pausada', completed: 'Concluída', cancelled: 'Cancelada', archived: 'Arquivada',
};
const STATUS_HEX = {
  draft: '#94a3b8', scheduled: '#7366ff', running: '#54ba4a',
  paused: '#FFAA05', completed: '#16C7F9', cancelled: '#f44336', archived: '#6c757d',
};

const W_METRICS = [
  { key: 'total',     label: 'Total',           icon: Target,      gradient: 'linear-gradient(26deg,#7366ff,#a26cf8)', colorClass: 'primary',   suffix: ''  },
  { key: 'running',   label: 'Em disparo',       icon: Send,        gradient: 'linear-gradient(26deg,#54ba4a,#29d9d5)', colorClass: 'success',   suffix: ''  },
  { key: 'completed', label: 'Concluídas',       icon: CheckCircle, gradient: 'linear-gradient(26deg,#04b1e0,#6ddaf1)', colorClass: 'info',      suffix: ''  },
  { key: 'rate',      label: 'Taxa de resposta', icon: TrendingUp,  gradient: 'linear-gradient(26deg,#f73164,#ff8fa0)', colorClass: 'secondary', suffix: '%' },
];

const BTN = {
  width: 30, height: 30, display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', padding: 0, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
};

export default function Campanhas() {
  const navigate = useNavigate();
  const { user } = useProspectAuth();
  const isAdmin  = user?.role === 'admin';
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilter] = useState('');

  async function fetchCampaigns() {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/campaigns${params}`);
      setCampaigns(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { fetchCampaigns(); }, [filterStatus]); // eslint-disable-line

  async function changeStatus(id, status) {
    try {
      await api.put(`/campaigns/${id}/status`, { status });
      fetchCampaigns();
    } catch (e) { alertErro('Erro', e.response?.data?.error || 'Erro ao alterar status'); }
  }

  async function handleLaunchDraft(id, name) {
    const ok = await alertConfirmar('Iniciar disparo?', `Iniciar disparo da campanha "${name}"?`);
    if (!ok) return;
    try {
      await api.put(`/campaigns/${id}/status`, { status: 'running' });
      fetchCampaigns();
      alertSucesso('Campanha iniciada!', `"${name}" está em disparo.`);
    } catch (e) { alertErro('Erro ao iniciar', e.response?.data?.error || ''); }
  }

  async function handleDelete(id, name) {
    const ok = await alertConfirmar(
      'Excluir campanha?',
      `"${name}" será removida permanentemente. Esta ação não pode ser desfeita.`
    );
    if (!ok) return;
    try {
      await api.delete(`/campaigns/${id}`);
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (e) { alertErro('Erro ao excluir', e.response?.data?.error || ''); }
  }

  async function handleArchive(id) {
    try {
      await api.put(`/campaigns/${id}/status`, { status: 'archived' });
      fetchCampaigns();
    } catch (e) { alertErro('Erro ao arquivar', e.response?.data?.error || ''); }
  }

  async function duplicate(id) {
    try {
      const { data } = await api.post(`/campaigns/${id}/duplicate`);
      navigate(`/campanhas/${data.id}`);
    } catch (e) { alertErro('Erro ao duplicar', e.response?.data?.error || ''); }
  }

  const filtered = campaigns.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  const running   = campaigns.filter(c => c.status === 'running').length;
  const completed = campaigns.filter(c => c.status === 'completed').length;
  const totalSent = campaigns.reduce((s, c) => s + (c.sent || 0), 0);
  const totalReply= campaigns.reduce((s, c) => s + (c.replied || 0), 0);
  const replyRate = totalSent > 0 ? parseFloat(((totalReply / totalSent) * 100).toFixed(1)) : 0;
  const metricValues = { total: campaigns.length, running, completed, rate: replyRate };

  return (
    <>
      <Breadcrumbs mainTitle='Campanhas' parent='Topo Prospect' title='Campanhas' />
      <Container fluid>

        {isAdmin && (
          <div className='alert alert-secondary py-2 px-3 mb-3 d-flex align-items-center gap-2' style={{ fontSize: 12, color: '#495057' }}>
            <span>🔍</span> <span>Modo auditoria — faça login como tenant para operar</span>
          </div>
        )}

        {/* Header */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <div />
          {!isAdmin && (
            <Button color='primary' size='sm' onClick={() => navigate('/campanhas/nova')}>
              <Plus size={14} className='me-1' /> Nova Campanha
            </Button>
          )}
        </div>

        {/* ── Widgets ────────────────────────────────────────────────────── */}
        <Row className='mb-2'>
          {W_METRICS.map(m => (
            <Col xl='3' md='6' sm='6' key={m.key} className='mb-4'>
              <Card className='widget-1'>
                <CardBody>
                  <div className='widget-content'>
                    <div className={`widget-round ${m.colorClass}`}>
                      <div className='bg-round' style={{ background: m.gradient, overflow: 'hidden', width: 54, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <m.icon color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                      </div>
                    </div>
                    <div>
                      <h4 className='mb-1 f-w-600'>
                        <CountUp end={metricValues[m.key]} duration={1.5} decimals={m.suffix === '%' ? 1 : 0} />{m.suffix}
                      </h4>
                      <span className='f-light f-12'>{m.label}</span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ── Tabela ─────────────────────────────────────────────────────── */}
        <Card>
          <CardBody>
            <div className='d-flex gap-3 mb-4 flex-wrap'>
              <InputGroup style={{ maxWidth: 280 }}>
                <span className='input-group-text bg-transparent'><Search size={14} /></span>
                <Input placeholder='Buscar campanha...' value={search} onChange={e => setSearch(e.target.value)} bsSize='sm' />
              </InputGroup>
              <Input type='select' bsSize='sm' style={{ width: 160 }} value={filterStatus} onChange={e => setFilter(e.target.value)}>
                <option value=''>Todos os status</option>
                {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Input>
            </div>

            {loading ? (
              <div className='text-center py-5 text-muted'>Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className='text-center py-5'>
                <TrendingUp size={48} className='mb-3 text-muted' />
                <h6 className='text-muted'>
                  {search || filterStatus ? 'Nenhuma campanha encontrada' : 'Nenhuma campanha criada ainda'}
                </h6>
                {!search && !filterStatus && !isAdmin && (
                  <>
                    <Button color='primary' size='sm' className='mt-2' onClick={() => navigate('/campanhas/nova')}>
                      Criar primeira campanha
                    </Button>
                    <p className='text-muted f-12 mt-2'>
                      Você precisará de pelo menos uma conexão WhatsApp e uma lista de contatos.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className='table-responsive'>
                <Table hover className='mb-0'>
                  <thead>
                    <tr>
                      <th>Campanha</th>
                      <th>Status</th>
                      <th>Progresso</th>
                      <th>Resposta</th>
                      <th>Data</th>
                      <th className='text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => {
                      const hex = STATUS_HEX[c.status] || '#94a3b8';
                      const rate = c.sent > 0 ? Math.round((c.replied / c.sent) * 100) : 0;
                      const pct  = c.total_contacts ? Math.min(100, ((c.sent || 0) / c.total_contacts) * 100) : 0;
                      return (
                        <tr key={c.id}>
                          {/* Nome + ícone */}
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <div className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                                style={{ width: 36, height: 36, background: hex + '18', color: hex }}>
                                <Target size={14} />
                              </div>
                              <div>
                                <button className='btn btn-link btn-sm p-0 text-start fw-semibold d-block' style={{ lineHeight: 1.3 }}
                                  onClick={() => navigate(`/campanhas/${c.id}`)}>
                                  {c.name}
                                </button>
                                <small className='text-muted d-block' style={{ fontSize: 11 }}>
                                  {c.total_contacts ?? 0} contatos
                                </small>
                              </div>
                            </div>
                          </td>

                          {/* Status badge */}
                          <td>
                            <span className='badge rounded-pill' style={{
                              background: hex + '18', color: hex,
                              border: `1px solid ${hex}44`, fontSize: 11, padding: '4px 10px',
                            }}>
                              {c.status === 'running' && (
                                <span className='me-1' style={{
                                  display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                                  background: hex, animation: 'pulse 1.5s infinite',
                                }} />
                              )}
                              {STATUS_LABEL[c.status]}
                            </span>
                          </td>

                          {/* Progresso */}
                          <td>
                            <small className='text-muted d-block mb-1'>{c.sent ?? 0} / {c.total_contacts ?? 0}</small>
                            <div className='progress' style={{ height: 5, width: 90, borderRadius: 10, background: '#f1f1f1' }}>
                              <div className='progress-bar' style={{ width: `${pct}%`, background: hex, borderRadius: 10 }} />
                            </div>
                          </td>

                          {/* Taxa */}
                          <td>
                            <span style={{ color: rate >= 20 ? '#54ba4a' : rate >= 5 ? '#ffaa05' : '#94a3b8', fontWeight: 600 }}>
                              {rate}%
                            </span>
                          </td>

                          {/* Data */}
                          <td className='text-muted'>
                            <small>{new Date(c.created_at).toLocaleDateString('pt-BR')}</small>
                          </td>

                          {/* Ações */}
                          <td style={{ width: 1, whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, width: 238 }}>

                              <button title='Ver detalhes'
                                style={{ ...BTN, background: 'none', border: '1px solid #dee2e6', color: '#6c757d' }}
                                onClick={() => navigate(`/campanhas/${c.id}`)}>
                                <Eye size={13} />
                              </button>

                              {!isAdmin && c.status === 'draft' && (
                                <button title='Editar campanha'
                                  style={{ ...BTN, background: 'none', border: '1px solid #7366ff', color: '#7366ff' }}
                                  onClick={() => navigate(`/campanhas/${c.id}/editar`)}>
                                  <Edit2 size={13} />
                                </button>
                              )}

                              {!isAdmin && (c.status === 'draft' || c.status === 'paused') && (
                                <button title={c.status === 'paused' ? 'Retomar' : 'Disparar agora'}
                                  style={{ ...BTN, background: 'none', border: '1px solid #54ba4a', color: '#54ba4a' }}
                                  onClick={c.status === 'draft' ? () => handleLaunchDraft(c.id, c.name) : () => changeStatus(c.id, 'running')}>
                                  <Play size={13} />
                                </button>
                              )}

                              {!isAdmin && c.status === 'running' && (
                                <button title='Pausar'
                                  style={{ ...BTN, background: 'none', border: '1px solid #FFAA05', color: '#FFAA05' }}
                                  onClick={() => changeStatus(c.id, 'paused')}>
                                  <Pause size={13} />
                                </button>
                              )}

                              {!isAdmin && ['running', 'paused', 'scheduled'].includes(c.status) && (
                                <button title='Cancelar'
                                  style={{ ...BTN, background: 'none', border: '1px solid #f44336', color: '#f44336' }}
                                  onClick={() => changeStatus(c.id, 'cancelled')}>
                                  <XCircle size={13} />
                                </button>
                              )}

                              {!isAdmin && ['completed', 'cancelled', 'paused'].includes(c.status) && (
                                <button title='Arquivar'
                                  style={{ ...BTN, background: 'none', border: '1px solid #6c757d', color: '#6c757d' }}
                                  onClick={() => handleArchive(c.id)}>
                                  <Archive size={13} />
                                </button>
                              )}

                              {!isAdmin && !['draft', 'archived'].includes(c.status) && (
                                <button title='Duplicar'
                                  style={{ ...BTN, background: 'none', border: '1px solid #dee2e6', color: '#6c757d' }}
                                  onClick={() => duplicate(c.id)}>
                                  <Copy size={13} />
                                </button>
                              )}

                              {!isAdmin && ['draft', 'cancelled', 'archived'].includes(c.status) && (
                                <button title='Excluir campanha'
                                  style={{ ...BTN, background: 'none', border: '1px solid #dc3545', color: '#dc3545' }}
                                  onClick={() => handleDelete(c.id, c.name)}>
                                  <Trash2 size={13} />
                                </button>
                              )}

                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            <div className='mt-3'>
              <small className='text-muted'>{filtered.length} campanha{filtered.length !== 1 ? 's' : ''}</small>
            </div>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
